import { createSlice, PayloadAction, Draft } from '@reduxjs/toolkit';
import {
  AutocompleteResult,
  SearchMaskDirection,
  SearchMaskLocation,
  StoreLocationField,
} from 'app/models/types/store/location';

export const defaultState: StoreLocationField = {
  autocomplete: {
    from: {
      current: [],
      loading: false,
      error: {
        present: false,
        id: null,
        level: null,
      },
    },
    to: {
      current: [],
      loading: false,
      error: {
        present: false,
        id: null,
        level: null,
      },
    },
  },
  selected: {
    // @ts-expect-error ignore
    from: {},
    // @ts-expect-error ignore
    to: {},
  },
  recentCities: {
    from: [],
    to: [],
  },
  popularCities: {
    from: [],
    to: [],
  },
  recentCitiesCookie: {
    from: 'recent-cities-from',
    to: 'recent-cities-to',
  },
  autoCompleteCache: {},
};

function applyLocationChange(
  state: Draft<StoreLocationField>,
  direction: SearchMaskDirection,
  location: SearchMaskLocation,
) {
  if (!location?.name) {
    return;
  }

  const newSelected = {
    ...state.selected,
    [direction]: location,
  };
  if (location.uuid === state.selected?.to.uuid && direction === 'from') {
    newSelected.to = { ...state.selected.from };
  }

  state.selected = newSelected;
}

export const locationSlice = createSlice({
  name: 'location',
  initialState: defaultState,
  reducers: {
    setLocation(
      state,
      {
        payload: { direction, location },
      }: PayloadAction<{ direction: SearchMaskDirection; location: SearchMaskLocation }>,
    ) {
      applyLocationChange(state, direction, location);
    },
    updateAutocompleteValue(
      state,
      {
        payload: { direction, location },
      }: PayloadAction<{ direction: SearchMaskDirection; location: SearchMaskLocation }>,
    ) {
      applyLocationChange(state, direction, location);
    },
    updateSearchLocation(
      state,
      {
        payload: { direction, location },
      }: PayloadAction<{
        direction: SearchMaskDirection;
        location: SearchMaskLocation;
        previousLocations?: {
          arrivalCity: SearchMaskLocation;
          departureCity: SearchMaskLocation;
        };
        isAutoUpdate?: boolean;
        isInit?: boolean;
      }>,
    ) {
      applyLocationChange(state, direction, location);
    },
    toggleAutocompleteLoading(
      state,
      {
        payload: { direction, loading },
      }: PayloadAction<{ direction: SearchMaskDirection; loading: boolean }>,
    ) {
      state.autocomplete = {
        ...state.autocomplete,
        [direction]: {
          ...state.autocomplete[direction],
          loading: loading,
        },
      };
    },
    clearLocation(
      state,
      { payload: { direction } }: PayloadAction<{ direction: SearchMaskDirection }>,
    ) {
      state.selected = {
        ...state.selected,
        [direction]: {},
      };
    },
    switchLocations(state) {
      state.selected = {
        from: state.selected.to,
        to: state.selected.from,
      };
    },
    reportAutocompleteError(
      state,
      {
        payload: { direction, id, level },
      }: PayloadAction<{ id: unknown; level: unknown; direction: SearchMaskDirection }>,
    ) {
      state.autocomplete = {
        ...state.autocomplete,
        [direction]: {
          ...state.autocomplete[direction],
          error: {
            ...state.autocomplete[direction].error,
            present: true,
            id,
            level,
          },
        },
      };
    },
    clearAutocompleteError(
      state,
      { payload: { direction } }: PayloadAction<{ direction: SearchMaskDirection }>,
    ) {
      state.autocomplete = {
        ...state.autocomplete,
        [direction]: {
          ...state.autocomplete[direction],
          error: {
            ...state.autocomplete[direction].error,
            present: false,
            id: null,
            level: null,
          },
        },
      };
    },
    storeAutocompleteResults(
      state,
      {
        payload: { direction, results },
      }: PayloadAction<{ direction: SearchMaskDirection; results: AutocompleteResult[] }>,
    ) {
      state.autocomplete = {
        ...state.autocomplete,
        [direction]: {
          current: results,
          error: {
            ...state.autocomplete[direction].error,
            present: false,
            id: null,
            level: null,
          },
        },
      };
    },
    clearAutocompleteResults(
      state,
      { payload: { direction } }: PayloadAction<{ direction: SearchMaskDirection }>,
    ) {
      state.autocomplete = {
        ...state.autocomplete,
        [direction]: {
          ...state.autocomplete[direction],
          current: [],
        },
      };
    },
    setRecentCities(
      state,
      {
        payload: { recentCities },
      }: PayloadAction<{ recentCities: { [key in SearchMaskDirection]: AutocompleteResult[] } }>,
    ) {
      state.recentCities = recentCities;
    },
    removeRecentCity(
      state,
      {
        payload: { direction, cityId },
      }: PayloadAction<{ direction: SearchMaskDirection; cityId: string }>,
    ) {
      state.recentCities = {
        ...state.recentCities,
        [direction]: [...state.recentCities[direction].filter(({ id }) => id !== cityId)],
      };
    },
    setPopularCities(
      state,
      {
        payload: { direction, popularCities },
      }: PayloadAction<{ popularCities: AutocompleteResult[]; direction: SearchMaskDirection }>,
    ) {
      state.popularCities = {
        ...state.popularCities,
        [direction]: popularCities,
      };
    },
    storeAutocompleteCache(
      state,
      { payload: { key, results } }: PayloadAction<{ key: string; results: AutocompleteResult[] }>,
    ) {
      state.autoCompleteCache = {
        ...state.autoCompleteCache,
        [key]: results,
      };
    },
  },
});

export default locationSlice.reducer;

export const {
  setLocation,
  updateSearchLocation,
  updateAutocompleteValue,
  toggleAutocompleteLoading,
  clearLocation,
  switchLocations,
  reportAutocompleteError,
  clearAutocompleteError,
  storeAutocompleteResults,
  clearAutocompleteResults,
  setRecentCities,
  removeRecentCity,
  setPopularCities,
  storeAutocompleteCache,
} = locationSlice.actions;
